import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiTool, FiUser, FiTrendingUp, FiTarget, FiUsers, FiMessageSquare, FiCalendar } = FiIcons;

const SolutionsSection = () => {
  const [activeTab, setActiveTab] = useState(0);

  const businessTypes = [
    {
      id: 0,
      title: "Manufacturers",
      icon: FiSettings,
      color: "blue",
      solutions: [
        "Lead funnels for distributorship inquiries",
        "Interactive product catalogue funnels",
        "WhatsApp pricing automation system",
        "Dealer & distributor onboarding funnels",
        "Automated follow-up sequences",
        "Factory visit appointment automation",
        "Payment reminder systems"
      ]
    },
    {
      id: 1,
      title: "Service Providers",
      icon: FiTool,
      color: "green",
      solutions: [
        "Service inquiry capture funnels",
        "Consultation booking automation",
        "WhatsApp quoting system",
        "CRM pipeline for service tracking",
        "Automated client onboarding",
        "Feedback & review automation",
        "Service completion workflows"
      ]
    },
    {
      id: 2,
      title: "Freelancers",
      icon: FiUser,
      color: "purple",
      solutions: [
        "Personal brand website builder",
        "Portfolio showcase funnels",
        "AI-powered follow-up sequences",
        "Proposal automation system",
        "Client appointment booking",
        "Payment gateway integration",
        "Project management workflows"
      ]
    },
    {
      id: 3,
      title: "Solopreneurs",
      icon: FiTrendingUp,
      color: "orange",
      solutions: [
        "High-converting sales funnels",
        "WhatsApp nurturing campaigns",
        "Video hosting for training content",
        "Automated webinar systems",
        "Community building platform",
        "Course creation & delivery",
        "Revenue optimization tools"
      ]
    }
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5">
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-r from-green-400 to-blue-400 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Tailor-Made Solutions for Every 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> MSME Segment</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose your business type to see exactly how FunnelsDone transforms your specific challenges into growth opportunities.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div 
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {businessTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setActiveTab(type.id)}
              className={`flex items-center space-x-3 px-6 py-4 rounded-full font-semibold transition-all duration-300 ${
                activeTab === type.id
                  ? `bg-${type.color}-500 text-white shadow-lg`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={type.icon} className="w-5 h-5" />
              <span>{type.title}</span>
            </button>
          ))}
        </motion.div>

        {/* Tab Content */}
        <motion.div 
          key={activeTab}
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Solutions List */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4 mb-8">
                <div className={`w-16 h-16 bg-${businessTypes[activeTab].color}-500 rounded-xl flex items-center justify-center`}>
                  <SafeIcon icon={businessTypes[activeTab].icon} className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">{businessTypes[activeTab].title}</h3>
                  <p className="text-gray-600">Specialized solutions for your business needs</p>
                </div>
              </div>

              <div className="space-y-4">
                {businessTypes[activeTab].solutions.map((solution, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-300"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <div className={`w-6 h-6 bg-${businessTypes[activeTab].color}-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <SafeIcon icon={FiTarget} className="w-3 h-3 text-white" />
                    </div>
                    <p className="text-gray-800 font-medium">{solution}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right: Visual Representation */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className={`bg-gradient-to-br from-${businessTypes[activeTab].color}-50 to-${businessTypes[activeTab].color}-100 rounded-3xl p-8 border border-${businessTypes[activeTab].color}-200`}>
                {/* Mock Dashboard */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-bold text-gray-900">Business Dashboard</h4>
                    <div className={`px-3 py-1 bg-${businessTypes[activeTab].color}-100 text-${businessTypes[activeTab].color}-700 rounded-full text-sm font-medium`}>
                      {businessTypes[activeTab].title}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <SafeIcon icon={FiUsers} className={`w-6 h-6 text-${businessTypes[activeTab].color}-500`} />
                        <span className="text-2xl font-bold text-gray-900">247</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Active Leads</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <SafeIcon icon={FiMessageSquare} className={`w-6 h-6 text-${businessTypes[activeTab].color}-500`} />
                        <span className="text-2xl font-bold text-gray-900">89%</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Response Rate</p>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="space-y-3">
                    <h5 className="font-semibold text-gray-800">Recent Activity</h5>
                    <div className="space-y-2">
                      {[
                        "New lead from product funnel",
                        "WhatsApp auto-reply sent",
                        "Appointment booked automatically",
                        "Follow-up sequence triggered"
                      ].map((activity, idx) => (
                        <div key={idx} className="flex items-center space-x-3 text-sm text-gray-600">
                          <div className={`w-2 h-2 bg-${businessTypes[activeTab].color}-500 rounded-full`}></div>
                          <span>{activity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <motion.div 
                  className={`absolute -top-4 -right-4 w-12 h-12 bg-${businessTypes[activeTab].color}-500 rounded-full flex items-center justify-center`}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                >
                  <SafeIcon icon={FiTarget} className="w-6 h-6 text-white" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <a 
            href="https://funnelsdone.com/cash" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg rounded-full hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Get Your Custom Business Solution
            <SafeIcon icon={FiCalendar} className="ml-2 w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionsSection;